import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { FirebaseUploadAdapterPlugin } from '../../../../../firebase/FirebaseUploadAdapter';
import { authFetch } from '../../../../../utils/cloudFunctionsAuth';

// Utility function to fix image styles in HTML
export const fixImageStyles = (html: string): string => {
  return html.replace(/<img([^>]+)>/gi, (imgTag, attrs) => {
    let newAttrs = attrs;

    // 1. Remove existing width, height, and style attributes
    newAttrs = newAttrs.replace(/\sheight="[^(")]*"/gi, '');
    newAttrs = newAttrs.replace(/\swidth="[^(")]*"/gi, '');
    newAttrs = newAttrs.replace(/\sstyle="[^(")]*"/gi, ''); // Remove existing style attribute

    // 2. Add a desired width attribute for Outlook, but DO NOT force a fixed height.
    // Forcing both width+height can distort logos/photos by breaking aspect ratio.
    const forcedWidth = "100";
    let attributesToAdd = ` width="${forcedWidth}"`;

    // 3. Add a very basic style attribute
    const basicStyles = "display:block; border:0; margin:0; padding:0; height:auto; max-width:100%;";
    attributesToAdd += ` style="${basicStyles}"`;
    
    // Ensure attributes are space-separated and clean up the final attribute string.
    // Prepend existing non-conflicting attributes, then add ours.
    // We are replacing all key attributes, so we can just prepend remaining attributes from newAttrs.
    newAttrs = newAttrs.trim().replace(/\s+/g, ' '); // Clean up existing attributes
    
    // Filter out any remaining class, src, alt etc. to re-add them.
    // This is safer than trying to regex-replace parts of a complex string.
    const tempElement = document.createElement('div');
    tempElement.innerHTML = `<img ${attrs}>`;
    const img = tempElement.firstChild as HTMLImageElement;
    
    let finalAttrs = '';
    if (img && img.src) finalAttrs += ` src="${img.src}"`;
    if (img && img.alt) finalAttrs += ` alt="${img.alt}"`;
    if (img && img.className) finalAttrs += ` class="${img.className}"`;
    // Add any other non-styling, non-sizing attributes you want to preserve, e.g., title
    // For now, keeping it to src, alt, class.

    return `<img${finalAttrs}${attributesToAdd}>`;
  });
};

interface SignatureEditModalProps {
  show: boolean;
  onHide: () => void;
  accountId: string;
  initialSignatureHtml: string;
  onSignatureSaved: () => void;
}

const editorConfiguration = {
  toolbar: [
    'undo', 'redo', '|', 'heading', '|', 'bold', 'italic', 'underline', 'strikethrough', '|',
    'link', 'bulletedList', 'numberedList', '|', 'blockQuote', 'insertTable', 'imageUpload', 'mediaEmbed', 'removeFormat'
  ],
  placeholder: 'Type or paste your content here!',
  licenseKey: 'GPL',
  extraPlugins: [FirebaseUploadAdapterPlugin],
};

const SignatureEditModal: React.FC<SignatureEditModalProps> = ({ 
  show, 
  onHide, 
  accountId, 
  initialSignatureHtml,
  onSignatureSaved
}) => {
  const [signatureHtml, setSignatureHtml] = useState(initialSignatureHtml || '');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (show) {
      if (signatureHtml !== (initialSignatureHtml || '')) {
        setSignatureHtml(initialSignatureHtml || '');
      }
      setError(null);
    }
  }, [show, initialSignatureHtml]);

  const handleSave = async () => {
    if (!accountId) {
      setError("Account ID is missing.");
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      // Fix image styles before saving
      const fixedSignatureHtml = fixImageStyles(signatureHtml);
      
      // Update signature via HTTP endpoint (callable functions are blocked at the edge)
      const resp = await authFetch('https://us-central1-web-construct-app.cloudfunctions.net/updateGraphMailAccountHttp', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({ mailbox: accountId, signatureHtml: fixedSignatureHtml }),
      });
      const data = (await resp.json()) as any;
      if (!data?.ok) throw new Error(data?.error || 'Failed to save signature');
      
      onSignatureSaved();
      onHide();
    } catch (err: any) {
      console.error("Error updating signature:", err);
      setError(`Failed to save signature: ${err.message}`);
    }
    setIsLoading(false);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Edit Email Signature</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <div className="alert alert-danger">{error}</div>}
        <p className="text-muted mb-2">Edit the signature. Paste from Outlook should work better now.</p>
        <CKEditor
          editor={ClassicEditor} 
          config={editorConfiguration}
          data={signatureHtml}
          onChange={(event, editor) => {
            const data = editor.getData();
            setSignatureHtml(data);
          }}
          disabled={isLoading}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isLoading}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={isLoading}>
          {isLoading ? 'Saving...' : 'Save Signature'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SignatureEditModal; 